import React, { FC, useCallback, useEffect } from 'react';
import { ExaminationApiContextProvider } from '@higo/common/lib/features/ExaminationPage/providers';
import { useExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/hooks';
import { isNil } from 'rambda';
import {
  ExaminationPageModel,
  ExaminationPageSectionId,
} from '@higo/common/lib/features/ExaminationPage/models';
import { coerceNumber } from '@higo/common/lib/utils';
import {
  AbdominalAuscultationSection,
  AnamnesisSection,
  CoughAuscultationSection,
  EarsExaminationSection,
  ExaminationPageContextSection,
  HeartAuscultationSection,
  LungsAuscultationSection,
  SkinExaminationSection,
  ThroatExaminationSection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import {
  ExaminationPageCovidModal,
  ExaminationPageGeneralSurveyModal,
  ExaminationPageHeader,
  ExaminationPageInfoBar,
  ExaminationPageInfoBarOuterContainer,
  ExaminationPageOverview,
} from '@higo/common/lib/features/ExaminationPage/components';
import { VitalSignsSection } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection';
import { HorizontalDivider, LoaderIcon, LogoIcon } from '@higo/ui';
import styled from 'styled-components';
import { media } from '@higo/ui/lib/theme';
import { useParams } from 'react-router-dom';
import { PartnerExaminationPageLayout } from 'pages/Partner/PartnerExaminationPage/components/PartnerExaminationPageLayout';
import { PartnerExaminationPageSkeleton } from 'pages/Partner/PartnerExaminationPage/components/PartnerExaminationPageSkeleton';
import { useCreateJWTAuthorizedAxios } from 'hooks/useCreateJWTAuthorizedAxios';
import { useExchangePartnerOTPForExamsJWTMutation } from 'pages/Partner/PartnerExaminationPage/hooks/useExchangePartnerOTPForJWTMutation';
import { useSelectJWTExaminationId } from 'hooks/useSelectJWTExaminationId';
import { FormattedMessage } from 'react-intl';
import { FullPageMessage } from 'components/FullPageMessage';

export const partnerExaminationPageUrlParams = {
  otp: 'otp',
};

const ContentContainer = styled('main')`
  display: grid;

  section {
    scroll-margin-top: 220px;

    ${media.desktop.min} {
      scroll-margin-top: 193px;
    }
  }

  ${media.desktop.min} {
    grid-template-columns: minmax(0, 1fr) minmax(2rem, 6.25rem) 20.5rem;
  }

  ${media.mobile.max}, ${media.tablet.max} {
    grid-template-columns: 1fr;
  }

  padding-bottom: 14.425rem; // allows for proper floating window position
`;

const ContextSectionWrapper = styled.div<{ bothFiles: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.bothFiles === true
      ? '2fr minmax(1rem, 6.25rem) 1fr'
      : '2fr minmax(1rem, 3rem) 3.5fr'};

  ${media.mobile.max} {
    grid-template-columns: 1fr;
  }
`;

const PartnerExaminationPageContent: FC<{
  model: ExaminationPageModel;
}> = ({ model }) => {
  //todo: fix deps issue
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isSectionAvailable = useCallback(
    (id: ExaminationPageSectionId) =>
      model.availableExaminationPageSections.has(id),
    [model],
  );

  const hasContextFile =
    !isNil(model.details.examination.contextPhotoFileId) ||
    !isNil(model.details.examination.contextVideoFileId);

  const hasBothContextFiles =
    !isNil(model.details.examination.contextPhotoFileId) &&
    !isNil(model.details.examination.contextVideoFileId);

  return (
    <>
      {model.answeredGeneralSurvey && (
        <ExaminationPageCovidModal
          answeredGeneralSurvey={model.answeredGeneralSurvey}
        />
      )}
      <ExaminationPageHeader>
        <PartnerExaminationPageLayout>
          <ExaminationPageOverview
            mode={model.mode}
            patientOverview={model.patientOverview}
            examinationId={model.details.examination.id}
            patient={model.details.patient}
            generalSurveySlot={
              model.answeredGeneralSurvey && (
                <ExaminationPageGeneralSurveyModal
                  answeredGeneralSurvey={model.answeredGeneralSurvey}
                  hasCopyFeature
                  hasToastWhenCopy={false}
                />
              )
            }
          />
        </PartnerExaminationPageLayout>
        <ExaminationPageInfoBarOuterContainer>
          <PartnerExaminationPageLayout>
            <ExaminationPageInfoBar
              examinationCreateDate={
                model.details.examination.sessionEndTime ??
                model.details.examination.createDate
              }
              higoMode={model.details.patient.type} // todo: mode shouldn't be stored on user but  ¯\_(ツ)_/¯
              executedBy={model.details.executedBy}
              hasOperatorData
            />
          </PartnerExaminationPageLayout>
        </ExaminationPageInfoBarOuterContainer>
      </ExaminationPageHeader>
      <PartnerExaminationPageLayout>
        <ContentContainer>
          <div>
            {model.mode === 'PRO' && (
              <VitalSignsSection
                examinationId={model.details.examination.id}
                physicalExaminationId={
                  model.details.examination.physicalExaminationId
                }
                isPhysicalExaminationCompletedFn={
                  model.isPhysicalExaminationCompletedFn
                }
              />
            )}
            {model.mode === 'FAMILY' && (
              <>
                <HorizontalDivider />
                {hasContextFile ? (
                  <ContextSectionWrapper bothFiles={hasBothContextFiles}>
                    <ExaminationPageContextSection
                      contextPhotoFileId={
                        model.details.examination.contextPhotoFileId
                      }
                      contextVideoFileId={
                        model.details.examination.contextVideoFileId
                      }
                      hasBothContextFiles={hasBothContextFiles}
                    />
                    <div />
                    <VitalSignsSection
                      examinationId={model.details.examination.id}
                      physicalExaminationId={
                        model.details.examination.physicalExaminationId
                      }
                      isPhysicalExaminationCompletedFn={
                        model.isPhysicalExaminationCompletedFn
                      }
                    />
                  </ContextSectionWrapper>
                ) : (
                  <VitalSignsSection
                    examinationId={model.details.examination.id}
                    physicalExaminationId={
                      model.details.examination.physicalExaminationId
                    }
                    isPhysicalExaminationCompletedFn={
                      model.isPhysicalExaminationCompletedFn
                    }
                  />
                )}
              </>
            )}

            {isSectionAvailable(ExaminationPageSectionId.AnamnesisSurvey) && (
              <AnamnesisSection
                examinationId={model.details.examination.id}
                mode={model.mode}
                isNotesCreateAndReadPermission={false}
                isNotesReadPermissionOnly={true}
                anamnesisTaggedQuestions={
                  model.answeredAnamnesisSurvey?.taggedQuestions
                }
                generalTaggedQuestions={
                  model.answeredGeneralSurvey?.taggedQuestions
                }
                hasCopyFeature
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Skin) && (
              <SkinExaminationSection
                examinationId={model.details.examination.id}
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Heart) && (
              <HeartAuscultationSection
                examinationId={model.details.examination.id}
                patientModel={model.patient}
                diagnosisModel={model.diagnosis}
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Lungs) && (
              <LungsAuscultationSection
                examinationId={model.details.examination.id}
                patientModel={model.patient}
                diagnosisModel={model.diagnosis}
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Cough) && (
              <CoughAuscultationSection
                examinationId={model.details.examination.id}
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Ears) && (
              <EarsExaminationSection
                examinationId={model.details.examination.id}
                isPhysicalExaminationCompletedFn={
                  model.isPhysicalExaminationCompletedFn
                }
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Throat) && (
              <ThroatExaminationSection
                examinationId={model.details.examination.id}
              />
            )}

            {isSectionAvailable(ExaminationPageSectionId.Abdominal) && (
              <AbdominalAuscultationSection
                examinationId={model.details.examination.id}
              />
            )}
          </div>
        </ContentContainer>
      </PartnerExaminationPageLayout>
    </>
  );
};

const PartnerExaminationPageDataLoader: FC<{ id: number }> = ({ id }) => {
  const examId = coerceNumber(id);
  const model = useExaminationPageModel(examId);

  return isNil(model) ? (
    <PartnerExaminationPageSkeleton />
  ) : (
    <PartnerExaminationPageContent model={model} />
  );
};

const PartnerExaminationPage = () => {
  const { [partnerExaminationPageUrlParams.otp]: otp } = useParams();
  const {
    isLoading,
    data: jwtToken,
    mutate,
  } = useExchangePartnerOTPForExamsJWTMutation();

  const examinationId = useSelectJWTExaminationId(jwtToken?.access_token);

  useEffect(() => {
    otp && mutate(otp);
  }, [otp, mutate]);

  const authorizedAxios = useCreateJWTAuthorizedAxios(jwtToken?.access_token);

  if (isLoading) {
    return (
      <FullPageMessage icon={<LoaderIcon />}>
        <FormattedMessage id="microsite.loadingExamination" />
      </FullPageMessage>
    );
  }

  if (!examinationId || !authorizedAxios) {
    return (
      <FullPageMessage icon={<LogoIcon />}>
        <FormattedMessage id="microsite.invalidTokenProvided" />
      </FullPageMessage>
    );
  } else {
    return (
      <ExaminationApiContextProvider value={authorizedAxios}>
        <PartnerExaminationPageDataLoader id={examinationId} />
      </ExaminationApiContextProvider>
    );
  }
};

export default PartnerExaminationPage;
